import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Checkbox, Form, FormInstance, Input, Select } from "antd";
import StyledModal from "../../../components/CustomModal";
import { FooterButtons } from "../../Category/styles";
import { StyledButton } from "../../../components/styles";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { lostReportStatusOptions } from "../utils/const";
import { sortOptionsAlphabetically } from "../../../utils/helpers/sortOptionsAlphabetically";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { fetchDeleteReasons } from "../../../redux/features/deleteReason/deleteReasonAsyncThunk";
import { UnlinkModalSubTitle } from "./styles";
import { NotFoundReason } from "../../../types/ReasonTypes";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { LostStatuses } from "../../../types/LostStatuses";
import { FooterButtons as ConfirmationModalFooterButtons } from "../../../components/ConfirmationModal/styles";

const { TextArea } = Input;

interface UnlinkAndUndoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (sendEmail: boolean) => void;
  loading?: boolean;
  form: FormInstance;
}

const UnlinkAndUndoModal: React.FC<UnlinkAndUndoModalProps> = ({ isOpen, onClose, onSave, loading = false, form }) => {
  const [showEmailSendConfirm, setShowEmailSendConfirm] = useState(false);

  const dispatch = useAppDispatch();
  const { report } = useAppSelector((state) => state.report);

  const statusOptionsWithDisabled = useMemo(() => lostReportStatusOptions, []);

  const filteredStatusOptions = useMemo(() => statusOptionsWithDisabled, [statusOptionsWithDisabled]);

  const currentStatusId = Form.useWatch("currentStatus", form);
  const currentStatus = useMemo(() => lostReportStatusOptions.find((status) => status.value === currentStatusId), [currentStatusId]);
  const newStatusId = Form.useWatch("newStatus", form);
  const newStatus = useMemo(() => lostReportStatusOptions.find((status) => status.value === newStatusId), [newStatusId]);
  const { data: deleteReasonData, status } = useAppSelector((state) => state.deleteReason);

  useEffect(() => {
    if (status === RequestResults.idle) {
      dispatch(fetchDeleteReasons());
    }
  }, [dispatch, status]);

  // Determine flow types based on the status change
  const isUndoFlow = useMemo(() => {
    if (!currentStatusId || !newStatusId) return false;
    return newStatusId < currentStatusId && newStatusId !== LostStatuses.Deleted;
  }, [currentStatusId, newStatusId]);

  const isDeleteFlow = useMemo(() => newStatusId === LostStatuses.Deleted, [newStatusId]);
  const isDeleteUndoFlow = useMemo(() => newStatusId === LostStatuses.Open && currentStatusId === LostStatuses.Deleted, [newStatusId, currentStatusId]);

  // Determine if unlink reason is required based on the flow diagram
  const isUnlinkReasonRequired = useMemo(() => {
    // Pre-Match to InProcess/Open: Unlink reason required
    // Final-Match to Pre-Match: No unlink reason required (normal flow)
    // Ready To Ship to Final-Match: No unlink reason required (normal flow)
    // Shipped to Ready To Ship: No unlink reason required (normal flow)
    // Pre-Match/Final-Match/Ready To Ship/Shipped to Delete: Unlink reason required

    return false;
  }, [currentStatusId, isUndoFlow, isDeleteFlow]);

  // Determine if delete/change reason is required
  const isDeleteReasonRequired = useMemo(() => {
    // Open to Delete: Reason required
    // InProcess to Delete: Reason required
    // Pre-Match to Delete: Reason required
    // Final-Match to Delete: Reason required
    // Ready To Ship to Delete: Reason required
    // Delete to InProcess: Reason required (undo flow)

    if (isDeleteFlow) {
      return true; // Always need reason when deleting
    }

    if (newStatusId === LostStatuses.Open && currentStatusId === LostStatuses.Deleted) {
      return true; // Need reason when undoing a delete
    }

    return false;
  }, [isDeleteFlow, newStatusId, currentStatusId]);

  const unlinkReason = Form.useWatch("unlinkReason", form);
  const reason = Form.useWatch("reason", form);
  const selectedDeleteReason = Form.useWatch("discardReason", form);
  const discardReasonText = Form.useWatch("discardReasonText", form);
  const deleteText = Form.useWatch("delete", form);

  const isFormValid = useMemo(() => {
    // Check if unlink reason is required and provided
    if (isUnlinkReasonRequired && (!unlinkReason || !unlinkReason.trim())) return false;

    // Check delete flow requirements
    if (isDeleteFlow) {
      // Check if delete reason is selected
      if (!selectedDeleteReason) return false;

      // Check if 'other' reason text is required and provided
      if (selectedDeleteReason?.toLowerCase() === "other" && (!discardReasonText || !discardReasonText.trim())) {
        return false;
      }

      // Check if 'delete' checkbox is checked
      if (!deleteText) {
        return false;
      }
    }

    return true;
  }, [isUnlinkReasonRequired, unlinkReason, isDeleteFlow, selectedDeleteReason, form, deleteText, discardReasonText]);

  // useEffect(() => {
  //   // Set validation rules based on the status change
  //   form.validateFields(['reason', 'unlinkReason']);
  // }, [isDeleteReasonRequired, isUnlinkReasonRequired, form]);

  const handleSave = () => {
    if (selectedDeleteReason?.toLowerCase()?.trim().includes(NotFoundReason.toLowerCase())) {
      setShowEmailSendConfirm(true);
    } else {
      onSave(isDeleteFlow ? false : true);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const deleteReasonOptions = useMemo(() => {
    return deleteReasonData?.map((deleteReason) => ({
      label: deleteReason.reason ?? "",
      value: deleteReason.reason ?? "",
      key: deleteReason.id?.toString() ?? "",
    }));
  }, [deleteReasonData]);

  const getAlertMessage = () => {
    if (isDeleteFlow) {
      return { title: "Are you sure you want to delete this item?", subTitle: "" };
    } else if (isUndoFlow && currentStatusId < LostStatuses.Deleted) {
      return {
        title: (
          <>
            Are you sure you want to change status from <span style={{ fontWeight: "bold" }}>{currentStatus?.label}</span> to <span style={{ fontWeight: "bold" }}>{newStatus?.label}</span>?
          </>
        ),
        subTitle: <>Your linked found item {report?.foundItem?.itemNumber} will remain the same</>,
      };
    } else {
      return {
        title: (
          <>
            Are you sure you want to change status from <span style={{ fontWeight: "bold" }}>{currentStatus?.label}</span> to <span style={{ fontWeight: "bold" }}>{newStatus?.label}</span>?
          </>
        ),
        subTitle: "",
      };
    }
  };

  const getButtonText = () => {
    if (isDeleteFlow) {
      return "Yes, Delete";
    } else if (isUndoFlow && isUnlinkReasonRequired) {
      return "Yes, Unlink & Change Status";
    } else if (isUndoFlow) {
      return "Yes, Change Status";
    } else {
      return "Yes, Change Status";
    }
  };

  const getModalTitle = () => {
    if (isDeleteFlow) {
      return "Delete Item";
    } else if (isUndoFlow && isUnlinkReasonRequired) {
      return "Unlink & Change Status";
    } else {
      return "Change Status";
    }
  };

  return (
    <StyledModal
      draggable={true}
      title={getModalTitle()}
      open={isOpen}
      onCancel={handleCancel}
      width={600}
      footer={
        <FooterButtons>
          <StyledButton onClick={handleCancel} disabled={loading}>
            No, Cancel
          </StyledButton>
          <StyledButton type="primary" danger={isDeleteFlow} onClick={handleSave} loading={loading} disabled={!isFormValid}>
            {getButtonText()}
          </StyledButton>
        </FooterButtons>
      }
      centered
    >
      <Form form={form} style={{ marginTop: "20px" }} layout="vertical" disabled={loading}>
        <Alert type={isDeleteFlow ? "error" : "info"} style={{ marginBottom: 16 }} showIcon message={getAlertMessage()?.title} />
        <Form.Item label={"Current Status"} name={"currentStatus"}>
          <Select size="large" options={filteredStatusOptions} disabled />
        </Form.Item>

        <Form.Item label={"New Status"} name={"newStatus"}>
          <Select size="large" options={filteredStatusOptions} disabled />
        </Form.Item>


        {isUnlinkReasonRequired && (
          <Form.Item label={"Reason for unlinking"} name={"unlinkReason"} rules={[{ required: true, message: "Please enter reason for unlinking" }]}>
            <TextArea size="large" rows={3} placeholder="Enter reason for unlinking the matched item" />
          </Form.Item>
        )}

        {isDeleteUndoFlow && (
          <Form.Item
            label="Recover Reason"
            name="recoverReason"
            rules={[{ required: true, message: "Please enter reason for recovery" }]}
          >
            <Input size="large" placeholder="Enter reason for recovery" />
          </Form.Item>
        )}

        {isDeleteFlow && (
          <>
            <Form.Item name="discardReason" label="Delete Reason" rules={[{ required: true, message: "Please select reason" }]}>
              <Select size="large" placeholder={"Select Reason for delete"} loading={status === RequestResults.pending} options={sortOptionsAlphabetically(deleteReasonOptions, "label")} />
            </Form.Item>

            {selectedDeleteReason?.toLowerCase() === "other" && (
              <Form.Item name="discardReasonText" label="Reason" rules={[{ required: true, message: "Please enter reason" }]}>
                <Input.TextArea size="large" placeholder="Reason" name="discardReasonText" />
              </Form.Item>
            )}

            <Form.Item
              name="delete"
              valuePropName="checked"
              rules={[
                {
                  validator(_, value) {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please check the box to confirm delete."));
                  },
                },
              ]}
            >
              <Checkbox id="delete">
                I confirm that I want to delete
              </Checkbox>
            </Form.Item>
          </>
        )}
      </Form>

      {showEmailSendConfirm && (
        <ConfirmationModal
          isModalOpen={showEmailSendConfirm}
          onCancel={() => setShowEmailSendConfirm(false)}
          message={`Do you want to send the guest an ${newStatus?.label} Email?`}
          title={"Send Email"}
          loading={loading}
          handleSave={() => onSave(true)}
          CancelButtonText="No"
          confirmButtonText="Yes"
          footerButton={
            <ConfirmationModalFooterButtons>
              <Button onClick={() => onSave(false)} disabled={loading}>{"No"}</Button>
              <Button type="primary" onClick={() => onSave(true)} loading={loading}>
                {"Yes"}
              </Button>
            </ConfirmationModalFooterButtons>
          }
        />
      )}
    </StyledModal>
  );
};

export default UnlinkAndUndoModal;
